<template>
  <BittsTable
    :columns="columns"
    :enable-cell-text-selection="true"
    :suppress-row-click-selection="true"
    :compress-columns="true"
    :passed-context="passedContext"
    selection-state="'all'"
    :rows="rows"
    row-selection="multiple"
    :row-height="64"
    :pagination="false"
    class="fade-in team-table"
  />
</template>

<script setup lang="ts">
import { BittsTable } from '@crossbeam/bitts';

import { ColDef } from '@ag-grid-community/core';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';

import TeamTableActionsCell from '@/components/team/TeamTableActionsCell.vue';
import TeamTableLoginMethodCell from '@/components/team/TeamTableLoginMethodCell.vue';
import TeamTableRoleCell from '@/components/team/TeamTableRoleCell.vue';
import TeamTableSeatCell from '@/components/team/TeamTableSeatCell.vue';
import TeamTableUserCell from '@/components/team/TeamTableUserCell.vue';

import useAuth from '@/composables/useAuth';
import { sortRows } from '@/constants/team';
import { useBillingStore, useRolesStore, useTeamStore } from '@/stores';
import { Invite, TeamAuthorization } from '@/types/team';
import { shallowObjectSearch } from '@/utils';

const { search = '' } = defineProps<{
  search?: string;
}>();
const emit =
  defineEmits<
    (
      e: 'update:selectedUserIds',
      selectedUserIds: Set<number | undefined>,
    ) => void
  >();

const billingStore = useBillingStore();
const teamStore = useTeamStore();
const rolesStore = useRolesStore();

const { rolesById } = storeToRefs(rolesStore);
const { hasScope } = useAuth();
const { isFreeTier } = storeToRefs(billingStore);

const { authorizations, redeemableInvites } = storeToRefs(teamStore);

const canEditUsers = computed(() => hasScope('write:members'));

/* Searched Rows */
const authsWithRoles = computed(() =>
  authorizations.value.map(attachRoleAndChecked),
);
const invitesWithRoles = computed(() =>
  redeemableInvites.value.map(attachRoleAndChecked),
);
const searchedAuths = computed(() =>
  searchByFields(authsWithRoles.value, ['email', 'first_name', 'last_name']),
);
const searchedInvites = computed(() =>
  searchByFields(invitesWithRoles.value, ['email']),
);
const rows = computed(() => [...searchedAuths.value, ...searchedInvites.value]);

type AuthOrInvite = (Invite | TeamAuthorization) & {
  user?: TeamAuthorization['user'];
};
function attachRoleAndChecked(authOrInvite: AuthOrInvite) {
  return {
    ...authOrInvite,
    roleName: rolesById.value[authOrInvite?.role_id || '']?.name,
    isChecked: selectedUserIds.value.has(authOrInvite.user?.id as never),
  };
}

/* Row selection */
/* Using AG Grid's native row selection does not work because the table is re-rendered when the search is applied.
Instead each row has an "isChecked" property that is used to render a checkbox as checked or not, and
calls the handlePickUser callback to set this state in the parent */
const selectedUserIds = ref<Set<number | undefined>>(new Set([]));

const headerRowChecked = ref(false);
const headerRowIndeterminate = ref(false);
function updateHeaderRow() {
  setTimeout(() => {
    /* Performance requirement for AG Grid */
    headerRowChecked.value =
      selectedUserIds.value.size === authsWithRoles.value.length;
    headerRowIndeterminate.value =
      !headerRowChecked.value && selectedUserIds.value.size > 0;
    emit('update:selectedUserIds', selectedUserIds.value);
  });
}
const passedContext = {
  handlePickUser(authOrInvite: AuthOrInvite) {
    const userId = authOrInvite.user?.id as never;
    if (selectedUserIds.value.has(userId)) {
      selectedUserIds.value.delete(userId);
    } else {
      selectedUserIds.value.add(userId);
    }
    updateHeaderRow();
  },
};

/* Columns Definitions + Sorting */
const columns = computed<ColDef[]>(() => {
  const base: ColDef[] = [
    {
      field: 'Name',
      cellRenderer: TeamTableUserCell,
      suppressMovable: true,
      autoHeight: true,
      sortable: true,
      sort: 'asc',
      headerComponentParams: {
        displayName: 'Name',
        showArrow: true,
        showCheckbox: canEditUsers.value,
        checked: headerRowChecked.value,
        indeterminate: headerRowIndeterminate.value,
        onChecked(val: boolean) {
          const newIds = val ? authsWithRoles.value.map((v) => v.user?.id) : [];
          selectedUserIds.value = new Set(newIds);
          updateHeaderRow();
        },
      },
      comparator: (_, __, { data: a }, { data: b }) => {
        const firstValue = a.user ? a.user.first_name : a.email;
        const secondValue = b.user ? b.user.first_name : b.email;
        if (firstValue === secondValue) return 0;
        return firstValue?.localeCompare(secondValue);
      },
      width: 300,
      suppressSizeToFit: true,
    },
    {
      field: 'Seat',
      cellRenderer: TeamTableSeatCell,
      suppressMovable: true,
      sortable: true,
      headerComponentParams: { displayName: 'Seat', showArrow: true },
      comparator: (_, __, { data: a }) => {
        const aRole = a.role_id;
        return aRole ? 1 : -1;
      },
      width: 160,
      suppressSizeToFit: true,
    },
    {
      field: 'Core Role',
      suppressMovable: true,
      headerComponentParams: {
        displayName: 'Full Access Role',
        showArrow: true,
      },
      cellRenderer: TeamTableRoleCell,
      sortable: true,
      comparator: (_, __, { data: a }, { data: b }) => {
        return sortRows(a.roleName?.toLowerCase(), b.roleName?.toLowerCase());
      },
    },
    {
      suppressMovable: true,
      field: 'Login Method',
      cellRenderer: TeamTableLoginMethodCell,
    },
    {
      suppressMovable: true,
      field: '',
      cellRenderer: TeamTableActionsCell,
    },
  ];

  if (!isFreeTier.value) {
    base.splice(3, 0, {
      field: 'Sales Role',
      suppressMovable: true,
      headerComponentParams: {
        displayName: 'Sales Role',
        icon: ['fak', 'sales'],
        showArrow: true,
      },
      cellRenderer: TeamTableRoleCell,
      sortable: true,
      comparator: (_, __, { data: a }, { data: b }) => {
        return sortRows(
          a.sales_edge_role?.toLowerCase(),
          b.sales_edge_role?.toLowerCase(),
        );
      },
    });
  }
  return base;
});

/* Helpers 🤝 */
function searchByFields(invitesOrAuths: AuthOrInvite[], fields: string[]) {
  return search === ''
    ? invitesOrAuths
    : invitesOrAuths.filter((inviteOrAuth) =>
        shallowObjectSearch(inviteOrAuth.user ?? inviteOrAuth, fields, search),
      );
}
</script>

<style lang="pcss" scoped>
.team-table {
  .ag-cell {
    padding: 0;
  }

  .bulk-actions,
  .interactivity {
    @apply flex justify-between items-end gap-8 ml-8;
  }
}
</style>

<style lang="pcss">
.team-table-disabled-tooltip {
  .ant-tooltip-content {
    @apply max-w-[200px];
  }
}

.team-table {
  .ag-header-cell .svg-inline--fa.fa-sales {
    @apply text-success-accent pr-2;
  }

  /* This is the "actions" cell, right-aligned */
  .ag-row .ag-cell:last-of-type {
    @apply justify-end;
  }
}
</style>
